import { create } from "zustand";
import { v4 as uuidv4 } from "uuid";

export type SetupWizardStep = "calibrate" | "confirm";

export type PendingCalibrationPoint = {
  /** Relative position on canvas (0-1) */
  x: number;
  y: number;
  /** Label for the point (A, B, C) */
  label: string;
  /** GPS coordinates (entered by user) */
  latitude?: number;
  longitude?: number;
};

interface SetupWizardState {
  /** Whether the setup wizard is active */
  isActive: boolean;
  /** Current step in the wizard */
  currentStep: SetupWizardStep;
  /** Calibration points being set up (max 3) */
  pendingPoints: PendingCalibrationPoint[];
  /** Index of point currently being edited (for GPS entry) */
  editingPointIndex: number | null;
  /** Screen position for GPS entry popover */
  popoverPosition: { x: number; y: number } | null;

  // Actions
  startWizard: () => void;
  exitWizard: () => void;
  setStep: (step: SetupWizardStep) => void;
  nextStep: () => void;
  prevStep: () => void;
  addPoint: (x: number, y: number) => void;
  updatePointGPS: (
    index: number,
    latitude: number,
    longitude: number
  ) => void;
  removePoint: (index: number) => void;
  clearPoints: () => void;
  setEditingPoint: (index: number | null, position?: { x: number; y: number }) => void;
  /** Get completed points with GPS coordinates (for scale calculation) */
  getCompletedPoints: () => Array<{
    x: number;
    y: number;
    latitude: number;
    longitude: number;
  }>;
  /** Get completed points as full CalibrationPoints (for saving to ramp) */
  getCalibrationPoints: (rampId: string) => Array<{
    id: string;
    ramp_id: string;
    x: number;
    y: number;
    latitude: number;
    longitude: number;
  }>;
}

const STEP_ORDER: SetupWizardStep[] = ["calibrate", "confirm"];
const POINT_LABELS = ["A", "B", "C"];

export const useSetupWizardStore = create<SetupWizardState>((set, get) => ({
  isActive: false,
  currentStep: "calibrate",
  pendingPoints: [],
  editingPointIndex: null,
  popoverPosition: null,

  startWizard: () =>
    set({
      isActive: true,
      currentStep: "calibrate",
      pendingPoints: [],
      editingPointIndex: null,
      popoverPosition: null,
    }),

  exitWizard: () =>
    set({
      isActive: false,
      currentStep: "calibrate",
      pendingPoints: [],
      editingPointIndex: null,
      popoverPosition: null,
    }),

  setStep: (step) => set({ currentStep: step }),

  nextStep: () => {
    const { currentStep } = get();
    const currentIndex = STEP_ORDER.indexOf(currentStep);
    if (currentIndex < STEP_ORDER.length - 1) {
      set({ currentStep: STEP_ORDER[currentIndex + 1] });
    }
  },

  prevStep: () => {
    const { currentStep } = get();
    const currentIndex = STEP_ORDER.indexOf(currentStep);
    if (currentIndex > 0) {
      set({ currentStep: STEP_ORDER[currentIndex - 1] });
    }
  },

  addPoint: (x, y) => {
    const { pendingPoints } = get();
    if (pendingPoints.length >= 3) return; // Max 3 points

    const newPoint: PendingCalibrationPoint = {
      x,
      y,
      label: POINT_LABELS[pendingPoints.length],
    };

    set({
      pendingPoints: [...pendingPoints, newPoint],
      editingPointIndex: pendingPoints.length,
    });
  },

  updatePointGPS: (index, latitude, longitude) => {
    const { pendingPoints } = get();
    if (index < 0 || index >= pendingPoints.length) return;

    const updatedPoints = [...pendingPoints];
    updatedPoints[index] = {
      ...updatedPoints[index],
      latitude,
      longitude,
    };

    set({
      pendingPoints: updatedPoints,
      editingPointIndex: null,
      popoverPosition: null,
    });
  },

  removePoint: (index) => {
    const { pendingPoints } = get();
    if (index < 0 || index >= pendingPoints.length) return;

    // Remove point and re-label remaining points
    const remainingPoints = pendingPoints
      .filter((_, i) => i !== index)
      .map((point, i) => ({
        ...point,
        label: POINT_LABELS[i],
      }));

    set({
      pendingPoints: remainingPoints,
      editingPointIndex: null,
      popoverPosition: null,
    });
  },

  clearPoints: () =>
    set({
      pendingPoints: [],
      editingPointIndex: null,
      popoverPosition: null,
    }),

  setEditingPoint: (index, position) =>
    set({
      editingPointIndex: index,
      popoverPosition: position ?? null,
    }),

  getCompletedPoints: () => {
    const { pendingPoints } = get();
    return pendingPoints
      .filter(
        (p): p is PendingCalibrationPoint & { latitude: number; longitude: number } =>
          p.latitude !== undefined && p.longitude !== undefined
      )
      .map((p) => ({
        x: p.x,
        y: p.y,
        latitude: p.latitude,
        longitude: p.longitude,
      }));
  },

  getCalibrationPoints: (rampId: string) => {
    const { pendingPoints } = get();
    return pendingPoints
      .filter(
        (p): p is PendingCalibrationPoint & { latitude: number; longitude: number } =>
          p.latitude !== undefined && p.longitude !== undefined
      )
      .map((p) => ({
        id: uuidv4(),
        ramp_id: rampId,
        x: p.x,
        y: p.y,
        latitude: p.latitude,
        longitude: p.longitude,
      }));
  },
}));
